import React from 'react'
import { Box, Button, Flex, Heading, Icon, SimpleGrid, Tag, Text } from '@chakra-ui/react'
import {
  LuAlarmClock, LuArrowRight, LuBarChart3, LuBell, LuInbox, LuLayers, LuMessageSquare,
  LuPenSquare, LuSmartphone, LuTags, LuTimer, LuUsers, LuWifiOff,
} from 'react-icons/lu'
import { BRAND_NAME } from '../../../theme/brand'

/**
 * Product overview of the Push / In-App SDK — what it is, why it exists and what it
 * offers. The landing view of the SDK Integration page, ahead of the per-platform
 * setup docs.
 *
 * Copy is the SDK OVERVIEW.md word for word (keep it in sync if that doc changes),
 * except that the product name is BRAND_NAME because the portal is white-labelled.
 */

const PURPOSES = [
  { icon: LuPenSquare, title: 'Messaging is authored centrally, not coded.', text: 'Teams create and change messages, rules, and reminders from the portal — no app release is needed for each one.' },
  { icon: LuTimer, title: 'The right message appears at the right moment.', text: "The SDK understands context and timing, so messages are shown when they're relevant rather than all at once." },
  { icon: LuWifiOff, title: "It keeps working when the network doesn't.", text: 'Reminders and messages still fire, and engagement is still measured, even when a user is offline.' },
  { icon: LuBarChart3, title: 'Everything is measured.', text: "Every message delivered and every interaction is captured, so the business can see what's working." },
]

const CAPABILITIES = [
  {
    icon: LuBell, color: 'blue', title: 'Push notifications',
    text: <>Reach users on their device even when the app is closed — announcements, alerts, offers, and time-sensitive nudges. Notifications can carry rich content such as images and media, offer quick action choices, and take a user straight to the right place in the app when tapped.</>,
  },
  {
    icon: LuInbox, color: 'purple', title: 'A message inbox',
    text: <>A built-in message center inside the app — a persistent place where users can find notifications and messages they may have missed, shown with an unread indicator so nothing important is lost.</>,
  },
  {
    icon: LuMessageSquare, color: 'teal', title: 'In-app messages',
    text: <>Messages that appear <b>inside the app while the user is using it</b> — a welcome, a tip, a promotion, a prompt — shown at the right moment based on what the user is doing. Because the app already knows the context, these messages appear instantly and only when they're relevant.</>,
  },
  {
    icon: LuAlarmClock, color: 'orange', title: 'On-device reminders',
    text: <>Reminders the <b>phone schedules and shows on its own</b> — before something expires, after a period of inactivity, around a recurring time, or in response to something the user did. Because the device keeps them, they fire reliably even when the app is closed and there's no connection, at no messaging cost.</>,
  },
  {
    icon: LuTags, color: 'pink', title: 'Tags for grouping and targeting',
    text: <>Label users with <b>tags</b> — reflecting who they are or what they've done, such as a loyal customer, a trial user, or someone who left an item behind. Tags can be applied or removed from within the app as a user's situation changes, and they feed straight into the platform's audience targeting. This lets a business aim its notifications, in-app messages, and reminders at exactly the right group of users rather than everyone.</>,
  },
  {
    icon: LuUsers, color: 'cyan', title: 'A single, consistent audience',
    text: <>One shared understanding of <b>who each user is</b>, used by every channel. A user receives a coherent experience across all of them, messages respect how often that person should be contacted, and when a user signs out the SDK cleanly forgets them and starts fresh — without ever carrying one person's information over to another.</>,
  },
  {
    icon: LuBarChart3, color: 'green', title: 'Built-in measurement',
    text: <>Delivery and interaction are captured for every channel and reported back to the platform, giving the business accurate engagement insight — and because this is recorded on the device first, nothing is lost when a user happens to be offline.</>,
  },
]

const brandTint = (alpha) => `rgb(var(--brand-primary-rgb) / ${alpha})`

const SectionTitle = ({ number, title }) => (
  <Flex align="center" gap={3} mb={5}>
    <Flex
      w="28px" h="28px" flexShrink={0} align="center" justify="center" borderRadius="full"
      bg="var(--brand-primary)" color="white" fontSize="sm" fontWeight="bold"
    >
      {number}
    </Flex>
    <Heading as="h2" fontSize="xl" fontWeight="semibold">{title}</Heading>
  </Flex>
)

const IconBadge = ({ icon, color }) => (
  <Flex
    w="40px" h="40px" flexShrink={0} align="center" justify="center" borderRadius="lg"
    bg={color ? `${color}.50` : brandTint(0.1)}
    color={color ? `${color}.500` : 'var(--brand-primary)'}
  >
    <Icon as={icon} boxSize="20px" />
  </Flex>
)

const Para = (props) => <Text fontSize="sm" color="gray.600" lineHeight="1.8" {...props} />

const SdkOverview = ({ onGetStarted }) => (
  <Box maxW="1100px">
    {/* Hero */}
    <Box
      position="relative"
      overflow="hidden"
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={brandTint(0.15)}
      bgGradient={`linear(135deg, ${brandTint(0.1)} 0%, ${brandTint(0.02)} 60%, white 100%)`}
      px={{ base: 6, md: 10 }}
      py={{ base: 8, md: 10 }}
    >
      <Icon
        as={LuSmartphone}
        position="absolute" right={{ base: '-30px', md: '30px' }} top="50%" transform="translateY(-50%)"
        boxSize={{ base: '140px', md: '180px' }} color={brandTint(0.08)} pointerEvents="none"
      />
      <Box position="relative" maxW="700px">
        <Tag size="sm" borderRadius="full" bg="white" color="var(--brand-primary)" borderWidth="1px" borderColor={brandTint(0.2)} mb={4}>
          Overview
        </Tag>
        <Heading as="h1" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" lineHeight="1.2" mb={3}>
          {BRAND_NAME} Push / In-App SDK
        </Heading>
        <Text fontSize="md" color="gray.600" lineHeight="1.7" mb={onGetStarted ? 6 : 0}>
          The engagement layer that lives inside a brand's mobile app. One SDK that lets a business reach,
          guide, and re-engage its users directly on their devices — through notifications, an in-app message
          center, in-the-moment messages, and reminders the phone keeps on its own. This document explains <b>what
          the SDK is</b>, <b>the purpose it serves</b>, and <b>the capabilities it offers</b>.
        </Text>
        {onGetStarted && (
          <Button
            size="md" bg="var(--brand-primary)" color="white" _hover={{ bg: 'var(--brand-primary-hover)' }}
            rightIcon={<LuArrowRight />} onClick={onGetStarted}
          >
            Start integrating
          </Button>
        )}
      </Box>
    </Box>

    {/* 1. What it is */}
    <Box mt={12}>
      <SectionTitle number={1} title="What it is" />
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <Flex gap={4} align="flex-start" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={5}>
          <IconBadge icon={LuSmartphone} />
          <Para>
            The {BRAND_NAME} Push / In-App SDK is the part of the {BRAND_NAME} platform that runs <b>inside a business's
            own mobile app</b>. The platform's portal is where a team decides who to reach, with what message, and under
            what rule; the SDK is what actually <b>delivers, shows, times, and measures</b> those messages on each
            user's device.
          </Para>
        </Flex>
        <Flex gap={4} align="flex-start" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={5}>
          <IconBadge icon={LuLayers} />
          <Para>
            It brings several engagement channels together into a <b>single, unified capability</b>. Rather than
            wiring up separate tools for push, in-app messaging, and reminders, a business integrates one SDK and
            switches on the channels it needs. Every channel shares the same audience, the same understanding of
            who each user is, and the same reliable, privacy-conscious behaviour.
          </Para>
        </Flex>
      </SimpleGrid>
    </Box>

    {/* 2. The purpose it serves */}
    <Box mt={12}>
      <SectionTitle number={2} title="The purpose it serves" />
      <Para mb={3} maxW="820px">
        Modern apps need to communicate with their users continuously — to welcome them, guide them, bring
        them back, remind them of something time-sensitive, and respond to what they do. Doing this well
        usually means stitching together multiple vendors and shipping an app update every time the message
        changes.
      </Para>
      <Para mb={5} maxW="820px">
        The SDK removes that friction. Its purpose is to give a business <b>one dependable way to engage users
        on their devices</b>, where:
      </Para>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {PURPOSES.map(p => (
          <Flex key={p.title} gap={4} align="flex-start" bg="gray.50" borderRadius="xl" p={5}>
            <IconBadge icon={p.icon} />
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>{p.title}</Text>
              <Text fontSize="sm" color="gray.600" lineHeight="1.6">{p.text}</Text>
            </Box>
          </Flex>
        ))}
      </SimpleGrid>
      <Box mt={5} borderLeft="3px solid" borderColor="var(--brand-primary)" bg={brandTint(0.05)} borderRadius="md" px={5} py={3}>
        <Text fontSize="sm" color="gray.700" fontWeight="medium">
          In short, it turns engagement from a one-off, release-bound effort into an ongoing, controllable
          capability.
        </Text>
      </Box>
    </Box>

    {/* 3. The capabilities it offers */}
    <Box mt={12}>
      <SectionTitle number={3} title="The capabilities it offers" />
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
        {CAPABILITIES.map(c => (
          <Box
            key={c.title}
            borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={5} bg="white"
            transition="all 0.15s ease"
            _hover={{ borderColor: `${c.color}.200`, boxShadow: 'md', transform: 'translateY(-2px)' }}
          >
            <IconBadge icon={c.icon} color={c.color} />
            <Text fontSize="md" fontWeight="semibold" mt={4} mb={2}>{c.title}</Text>
            <Text fontSize="sm" color="gray.600" lineHeight="1.6">{c.text}</Text>
          </Box>
        ))}
      </SimpleGrid>
    </Box>

    {/* 4. In summary */}
    <Box mt={12} borderRadius="2xl" bg={brandTint(0.06)} borderWidth="1px" borderColor={brandTint(0.15)} px={{ base: 6, md: 8 }} py={7}>
      <SectionTitle number={4} title="In summary" />
      <Text fontSize="sm" color="gray.700" lineHeight="1.8" maxW="900px">
        The {BRAND_NAME} Push / In-App SDK is a <b>single engagement layer for mobile apps</b>. It lets a business
        reach users with notifications, keep a message center inside the app, show the right message in the
        moment, and schedule reminders the phone honours on its own — all aimed at the right users through
        tags, authored from one place, tied to one consistent view of each user, measured end to end, and
        dependable even offline.
      </Text>
    </Box>
  </Box>
)

export default SdkOverview
